import { FC, PropsWithChildren, useCallback, useContext, useEffect, useState } from "react";

import * as amplitude from "@amplitude/analytics-browser";
import { Experiment, ExperimentClient } from "@amplitude/experiment-js-client";
import { AMPLITUDE_EXPERIMENT_CLIENT_CONFIG, camelCaseToSnakeCase, AMPLITUDE_GROUP_TYPE } from "@simplyk/common";
import {
  browserName,
  browserVersion,
  fullBrowserVersion,
  mobileModel,
  mobileVendor,
  osVersion,
  osName,
} from "react-device-detect";

import { AMPLITUDE_API_KEY, AMPLITUDE_EXPERIMENT_API_KEY, AmplitudeEvents } from "../constants/amplitude";
import { AmplitudeContext, GetExperimentValueProps, Primitive } from "../contexts/AmplitudeContext";
import { ConsentModeContext } from "../contexts/ConsentModeContext";

import { LightAmplitudeProvider } from "./LightAmplitudeProvider";

import { isProduction, isStaging, isTest } from "@/constants/env";
import { useCurrentUserContext } from "@/contexts/CurrentUserContext";
import { SessionContext } from "@/contexts/SessionContext";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const RealAmplitudeProvider: FC<PropsWithChildren> = ({ children }) => {
  const [experimentClient, setExperimentClient] = useState<ExperimentClient | null>(null);
  const [isAmplitudeLoading, setIsAmplitudeLoading] = useState<boolean>(!isTest);
  const { hasFunctionalityConsentMode } = useContext(ConsentModeContext);

  const { sessionId, isFraudulent } = useContext(SessionContext);
  const { organization, currentUser } = useCurrentUserContext();
  const { isSmallScreen } = useMediaQuery();

  const currentUserOrganizationId = organization?.id;

  useEffect(() => {
    if (isTest) {
      return;
    }
    const initAmplitude = async () => {
      try {
        if (hasFunctionalityConsentMode && AMPLITUDE_API_KEY && AMPLITUDE_EXPERIMENT_API_KEY) {
          setIsAmplitudeLoading(true);
          const { plugin: engagementPlugin } = await import("@amplitude/engagement-browser");
          amplitude.add(engagementPlugin());

          const userId = currentUser?.id;

          amplitude.init(AMPLITUDE_API_KEY, {
            defaultTracking: {
              pageViews: false,
              formInteractions: false,
              fileDownloads: false,
              sessions: true,
            },
            userId,
          });
          const experiment = Experiment.initializeWithAmplitudeAnalytics(AMPLITUDE_EXPERIMENT_API_KEY, {
            fetchOnStart: true,
            automaticFetchOnAmplitudeIdentityChange: true,
            ...AMPLITUDE_EXPERIMENT_CLIENT_CONFIG({ fetchTimeoutMillis: 4000 }),
          });
          await experiment.start();
          setExperimentClient(experiment);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error initializing Amplitude", error);
      } finally {
        setIsAmplitudeLoading(false);
      }
    };
    initAmplitude();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFunctionalityConsentMode]);

  const getExperimentValue = useCallback(
    async ({
      flagKey,
      userId,
      organizationId,
      userProperties,
      organizationProperties,
      signal,
    }: GetExperimentValueProps) => {
      // This fetch is necessary to make sure we set the props on the user and get the correct
      // variant value based on the user properties. It seems that identify does not populate
      // the data on the user in time.
      const userProps = userProperties ? formatAmplitudeProperties(userProperties) : undefined;
      const groupProps = organizationProperties ? formatAmplitudeProperties(organizationProperties) : undefined;

      // Check if the operation was cancelled before proceeding
      if (signal?.aborted) {
        return null;
      }

      try {
        await experimentClient?.fetch({
          user_id: userId,
          groups: organizationId ? { [AMPLITUDE_GROUP_TYPE]: [organizationId] } : undefined,
          user_properties: userProps,
          group_properties:
            organizationId && groupProps
              ? {
                  [AMPLITUDE_GROUP_TYPE]: {
                    [organizationId]: groupProps,
                  },
                }
              : undefined,
        });

        return experimentClient?.variant(flagKey);
      } catch (error) {
        if (signal?.aborted) {
          // Check if the operation was cancelled during the fetch. If so,
          return null;
        }
        throw error;
      }
    },
    [experimentClient]
  );

  const logAmplitudeEvent = useCallback(
    (event: AmplitudeEvents | string, payload?: { [key: string]: unknown }) => {
      if (isTest || isFraudulent || !hasFunctionalityConsentMode || typeof window === "undefined") {
        return;
      }
      const fullPayload = {
        ...payload,
        zeffySessionId: sessionId,
        device: isSmallScreen ? "mobile" : "desktop",
        browser: browserName,
        browser_version: browserVersion,
        full_browser_version: fullBrowserVersion,
        mobile_vendor: mobileVendor,
        mobile_model: mobileModel,
        os_name: osName,
        os_version: osVersion,
        user_agent: window.navigator?.userAgent,
      };
      if (!isProduction) {
        // eslint-disable-next-line no-console
        console.log(event, fullPayload);
      }
      amplitude.track({
        event_type: event,
        event_properties: fullPayload,
        groups: currentUserOrganizationId ? { organization: currentUserOrganizationId } : undefined,
      });
    },
    [isFraudulent, hasFunctionalityConsentMode, sessionId, isSmallScreen, currentUserOrganizationId]
  );

  // used to reset the amplitude user on signout
  const resetAmplitude = useCallback(() => {
    if (isTest || isFraudulent || !hasFunctionalityConsentMode) {
      return;
    }
    amplitude.reset();
  }, [hasFunctionalityConsentMode, isFraudulent]);

  return (
    <AmplitudeContext.Provider value={{ isAmplitudeLoading, getExperimentValue, logAmplitudeEvent, resetAmplitude }}>
      {children}
    </AmplitudeContext.Provider>
  );
};

const formatAmplitudeProperties = (props: Record<string, Primitive>): { [propertyName: string]: Primitive } => {
  const userProps: { [propertyName: string]: Primitive } = {};
  Object.keys(props).forEach((key) => {
    const v = props[key as keyof typeof props];
    // eslint-disable-next-line eqeqeq
    if (v === null || v === undefined) {
      return;
    }

    const k = camelCaseToSnakeCase(key);
    userProps[k] = v.toString();
  });

  return userProps;
};

// Export wrapper that conditionally uses LightAmplitudeProvider in dev mode
export const AmplitudeProvider: FC<PropsWithChildren> = ({ children }) => {
  // Only use LightAmplitudeProvider in development mode when the env var is set
  // Never use it in production or staging
  const useLightMode = !isProduction && !isStaging && process.env.NEXT_PUBLIC_AMPLITUDE_LIGHT_MODE === "true";

  if (useLightMode) {
    return <LightAmplitudeProvider>{children}</LightAmplitudeProvider>;
  }

  return <RealAmplitudeProvider>{children}</RealAmplitudeProvider>;
};
